import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useEffect, useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./columns";
import { useNavigate } from "react-router-dom";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { Provider } from "@lib/types/Provider";
import { ProviderModal } from "@lib/components/PageComponents/Provider";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableProps } from "antd";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { SharedContextProps } from "@lib/types/Context";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  apiAddProvider,
  apiDeleteProvider,
  apiGetProviders,
  apiUpdateProvider,
} from "@lib/api/provider";
import { toast } from "react-toastify";
import { useApi } from "@lib/hooks/APIHooks";

const SharedPaymentProviders: React.FC<SharedContextProps> = ({
  context,
  customTitle,
  minusHeight,
  pageIsActive = true,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { queriesCacheClear } = useApi();

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
  });

  const queryKeys = ["provider", JSON.stringify(params)];

  const providersQuery = useQuery({
    queryFn: () => apiGetProviders(params),
    queryKey: queryKeys,
    enabled: !!params && pageIsActive,
  });
  const { data, isLoading, isFetching } = providersQuery;
  const apiData = data?.data?.data;

  const addProviderMutation = useMutation({
    mutationFn: apiAddProvider,
    mutationKey: ["provider"],
    onSuccess() {
      toast.success(
        (messages["common.createdMessage"] as string).replace(
          "{name}",
          messages["common.provider"] as string,
        ),
      );
      // queryClient.invalidateQueries({
      //   queryKey: queryKeys,
      // });
      queriesCacheClear({ search: "provider" });
    },
  });

  const deleteProviderMutation = useMutation({
    mutationFn: apiDeleteProvider,
    mutationKey: ["provider"],
    onSuccess() {
      toast.success(
        (messages["common.deletedMessage"] as string).replace(
          "{name}",
          messages["common.provider"] as string,
        ),
      );
      // queryClient.invalidateQueries({
      //   queryKey: queryKeys,
      // });
      queriesCacheClear({ search: "provider" });
    },
  });

  const updateProviderMutation = useMutation({
    mutationFn: apiUpdateProvider,
    mutationKey: ["provider"],
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["common.provider"] as string,
        ),
      );
      // queryClient.invalidateQueries({
      //   queryKey: queryKeys,
      // });
      queriesCacheClear({ search: "provider" });
    },
  });

  const [openModal, setModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider>();

  useEffect(() => {
    if (!openModal) setSelectedProvider(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelectProvider = (provider: Provider) => {
    setSelectedProvider(provider);
    toggle(true);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        context,
        handleSelectProvider,
        navigate,
        deleteMutation: deleteProviderMutation,
        openModal,
        messages,
        apiData,
        updateMutation: updateProviderMutation,
      }),
    [apiData, params],
  );

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter.map((f) => ({
      key: f.split("||")[0],
      title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
      operator: f.split("||")[1],
      value: f.split("||")[2],
    })) as TableHeaderFilter[];
  }, [params]);

  const title = customTitle || (messages["provider.list"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={providersQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          onChange={onChange}
          rowKey={"id"}
          loading={isLoading || isFetching}
          minusHeight={minusHeight}
        />
      </AppsContent>

      <ProviderModal
        mutationQuery={
          selectedProvider ? updateProviderMutation : addProviderMutation
        }
        openModal={openModal}
        toggleModal={toggle}
        provider={selectedProvider}
      />
    </AppsContainer>
  );
};

export default SharedPaymentProviders;
